import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createTeamMember,
  fetchTeamMembers,
  updateTeamMember,
  deleteTeamMember,
} from '../redux/slices/teamSlice';
import '../modalstyles/AddTeamsModal.css';

const AddTeamsModal = ({ onClose }) => {
  const dispatch = useDispatch();
  const { list: teams, loading } = useSelector((state) => state.team);

  const [team, setTeam] = useState(initialState());
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [preview, setPreview] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  function initialState() {
    return {
      name: '',
      designation: '',
      file: null,
      linkedin: '',
      twitter: '',
      instagram: '',
      facebook: '',
    };
  }

  useEffect(() => {
    dispatch(fetchTeamMembers());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setTeam((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setTeam((prev) => ({ ...prev, file: file }));
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSave = async () => {
    if (!team.name || !team.designation) {
      alert('Name and designation are required.');
      return;
    }

    const formData = new FormData();
    formData.append('name', team.name);
    formData.append('designation', team.designation);
    formData.append('linkedin', team.linkedin);
    formData.append('twitter', team.twitter);
    formData.append('instagram', team.instagram);
    formData.append('facebook', team.facebook);
    if (team.file) {
      formData.append('file', team.file);
    }

    try {
      const action = editingId
        ? updateTeamMember({ id: editingId, formData })
        : createTeamMember(formData);

      await dispatch(action).unwrap();
      await dispatch(fetchTeamMembers());
      setTeam(initialState());
      setEditingId(null);
      setPreview(null);
    } catch (error) {
      console.error('Failed to save team member:', error);
    }
  };

  const handleEdit = (member) => {
    setTeam({
      name: member.name || '',
      designation: member.designation || '',
      file: null,
      linkedin: member.linkedin || '',
      twitter: member.twitter || '',
      instagram: member.instagram || '',
      facebook: member.facebook || '',
    });
    setEditingId(member.id);
    setPreview(member.photo ? `http://213.210.37.235:3000/${member.photo}` : null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this member?')) {
      await dispatch(deleteTeamMember(id));
      await dispatch(fetchTeamMembers());
    }
  };

  const filteredTeams = Array.isArray(teams)
    ? teams.filter((member) =>
        (member.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (member.designation || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const totalPages = Math.ceil(filteredTeams.length / itemsPerPage);
  const paginatedTeams = filteredTeams.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="AddTeamsModal-backdrop">
      <div className="AddTeamsModal-container">
        <div className="AddTeamsModal-header">
          <h2>{editingId ? 'Edit Team Member' : 'Add Team Member'}</h2>
          <button onClick={onClose} className="admin-tech-close-btn">&times;</button>
        </div>

        <div className="AddTeamsModal-content">
          <div className="AddTeamsModal-form">
            <input name="name" placeholder="Name" value={team.name} onChange={handleChange} />
            <input name="designation" placeholder="Designation" value={team.designation} onChange={handleChange} />
            <input type="file" accept="image/*" onChange={handleFileChange} />

            {preview && (
              <img
                src={preview}
                alt="Preview"
                style={{ width: 80, height: 80, objectFit: 'cover', margin: '10px 0' }}
              />
            )}

            <input name="linkedin" placeholder="LinkedIn" value={team.linkedin} onChange={handleChange} />
            <input name="twitter" placeholder="Twitter" value={team.twitter} onChange={handleChange} />
            <input name="instagram" placeholder="Instagram" value={team.instagram} onChange={handleChange} />
            <input name="facebook" placeholder="Facebook" value={team.facebook} onChange={handleChange} />

            <div className="form-actions">
              <button className="save-btn" onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : editingId ? 'Update' : 'Save'}
              </button>
              <button
                className="clear-btn"
                onClick={() => {
                  setTeam(initialState());
                  setEditingId(null);
                  setPreview(null);
                }}
              >
                Clear
              </button>
            </div>
          </div>

          <input
            className="search-input"
            type="text"
            placeholder="Search by name/designation"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />

          <div className="table-controls">
            <label>
              Items per page:
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={15}>15</option>
              </select>
            </label>
          </div>

          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>SN</th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedTeams.map((member, index) => (
                  <tr key={member.id}>
                    <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                    <td>
                      {member.photo ? (
                        <img
                          src={`http://213.210.37.235:3000/${member.photo}`}
                          alt="pic"
                          style={{ width: 50, height: 50, objectFit: 'cover' }}
                        />
                      ) : 'N/A'}
                    </td>
                    <td>{member.name}</td>
                    <td>{member.designation}</td>
                    <td>
                      <button onClick={() => handleEdit(member)} className="edit-btn">Edit</button>
                      <button onClick={() => handleDelete(member.id)} className="delete-btn">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="pagination">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTeamsModal;
